import { inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatMessage } from '../model/chat-message.interface';
import { GameThemeService } from './game-theme.service';
import { UserData } from '../model/user-data.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userLevel = signal<number | null>(null);
  public userAttempts = signal<number | null>(null);
  public userAchievements = signal<string[] | null>(null);
  public allLevels = signal<number[] | null>(null);
  public allCompletedLevels = signal<number[] | null>(null);
  public chatHistory = signal<ChatMessage[] | null>(null); // Chat history is always an array of ChatMessage
  public userTotalScore = signal<{ score: string; total_attempts: number; total_pings: number } | null>(null); // total scores for attempts, pings, promotions
  public userLevelDuration = signal<number | null>(null);
  public pingStatus = signal<boolean | null>(null);
  public userHint = signal<number | null>(null);
  public usedHints = signal<number | null>(null);
  public fileDownloaded = signal<{ download_type: string } | null>(null);
  private userData = signal<UserData | null>(null);
  private readonly gameThemeService = inject(GameThemeService);
  private readonly http = inject(HttpClient);
  private readonly baseUrl = this.gameThemeService.getBackendApiUrl();
  private readonly imagePath = this.gameThemeService.getImagePath();
  private readonly authService = inject(AuthService);
  private levelMutationVersion = 0;
  private pendingLevelChange: number | null = null;

  /**
   * Retrieves the level information of a user
   */
  getUserLevel(): void {
    const requestMutationVersion = this.levelMutationVersion;
    const expectedLevelDuringMutation = this.pendingLevelChange;

    this.http.get<{ body: { level: number } }>(`${this.baseUrl}/level`).subscribe({
      next: (response) => {
        if (requestMutationVersion !== this.levelMutationVersion) {
          return;
        }

        if (expectedLevelDuringMutation !== null && response.body.level !== expectedLevelDuringMutation) {
          return;
        }

        this.userLevel.set(response.body.level);
      },
      error: (error) => {
        console.error('Error retrieving user level:', error);
      },
    });
  }

  getAllLevels(): void {
    this.http.get<{ body: { levels: number[] } }>(`${this.baseUrl}/all_levels`).subscribe({
      next: (response) => {
        this.allLevels.set(response.body.levels);
      },
      error: (error) => {
        console.error('Error retrieving all levels:', error);
      },
    });
  }

  getAllCompletedLevels(): void {
    this.http.get<{ body: { levels: number[] } }>(`${this.baseUrl}/all_levels_completed`).subscribe({
      next: (response) => {
        this.allCompletedLevels.set(response.body.levels);
      },
      error: (error) => {
        console.error('Error retrieving all levels:', error);
      },
    });
  }

  /**
   * Retrieves the attempts of a user
   */
  getUserAttempts(): void {
    this.http.get<{ body: { attempts: number } }>(`${this.baseUrl}/attempts`).subscribe({
      next: (response) => {
        this.userAttempts.set(response.body.attempts);
      },
      error: (error) => {
        console.error('Error retrieving user attempts:', error);
      },
    });
  }

  /**
   * Retrieves the achievements of a user
   */
  getUserAchievements(): void {
    this.http.get<{ body: { achievements: string[] } }>(`${this.baseUrl}/achievements`).subscribe({
      next: (response) => {
        this.userAchievements.set(response.body.achievements);
      },
      error: (error) => {
        console.error('Error retrieving user achievements:', error);
      },
    });
  }

  /**
   * Retrieves the chat history of a user
   */
  getUserHistory(): void {
    this.http.get<{ body: { history: unknown[] } }>(`${this.baseUrl}/history`).subscribe({
      next: (response) => {
        const backendHistory: ChatMessage[] = (response.body.history || []) as ChatMessage[];
        const transformedHistory = this.transformImageTags(backendHistory);
        const currentHistory = this.chatHistory() || [];
        const persistentMessages = currentHistory.filter((msg: ChatMessage) => msg.is_persistent);
        if (persistentMessages.length > 0) {
          const combinedHistory = [...persistentMessages, ...transformedHistory];
          this.chatHistory.set(combinedHistory);
        } else {
          this.chatHistory.set(transformedHistory);
        }
      },
      error: (error) => {
        console.error('Error retrieving user history:', error);
      },
    });
  }

  /**
   * Retrieves the score of a user
   */
  getTotalScore(): void {
    this.http
      .get<{ body: { score: string; total_attempts: number; total_pings: number } }>(`${this.baseUrl}/score`)
      .subscribe({
        next: (response) => {
          this.userTotalScore.set(response.body);
        },
        error: (error) => {
          console.error('Error retrieving user score:', error);
        },
      });
  }

  /**
   * Retrieves the level duration of a user
   * http://127.0.0.1:8000/user/userID/level_duration?level=1
   */
  getUserLevelDuration(): void {
    this.http.get<{ body: { level_duration: number } }>(`${this.baseUrl}/level_duration`).subscribe({
      next: (response) => {
        this.userLevelDuration.set(response.body.level_duration);
      },
      error: (error) => {
        console.error('Error retrieving user level duration:', error);
      },
    });
  }

  /**
   * Sends a ping to the server and updates the pingStatus signal
   */
  pingBackend(): void {
    this.http.post<boolean>(`${this.baseUrl}/ping`, true).subscribe({
      next: (response) => {
        this.pingStatus.set(response);
      },
      error: (error) => {
        console.error('Error pinging server:', error);
        this.pingStatus.set(false);
      },
    });
  }

  postHint(): void {
    this.http.post<{ body: { status: number } }>(`${this.baseUrl}/hint`, true).subscribe({
      next: (response) => {
        this.userHint.set(response.body.status);
      },
      error: (error) => {
        console.error('Error getting hint:', error);
      },
    });
  }

  /**
   * Retrieves the number of used hints
   */
  getHints(): void {
    this.http.get<{ body: { hint: number } }>(`${this.baseUrl}/hint`).subscribe({
      next: (response) => {
        this.usedHints.set(response.body.hint);
      },
      error: (error) => {
        console.error('Error retrieving user hints:', error);
      },
    });
  }

  public loadUser(): void {
    const { firstName, lastName, fullName, userName, initials } = this.createUserNames();
    this.userData.set({ firstName, lastName, fullName, userName, initials });
  }

  /**
   * Creates a new user in the backend
   */
  createUser(): void {
    this.http.post<{ body: { first_name: string; last_name: string } }>(`${this.baseUrl}/create_user`, {}).subscribe({
      next: (response) => {
        console.log('User created successfully:', response.body);
      },
      error: (error) => {
        console.error('Error creating user:', error);
      },
    });
  }

  public getUserData(): Signal<UserData | null> {
    if (this.userData()?.initials === '??') {
      this.loadUser();
    }
    return this.userData;
  }

  /**
   * Sets a new level for the user (creates user automatically if not exists)
   * @param level The level to set
   */
  async setLevel(level: number): Promise<void> {
    this.levelMutationVersion++;
    this.pendingLevelChange = level;
    this.userLevel.set(level);

    try {
      await firstValueFrom(this.http.post<{ body: { status: boolean } }>(`${this.baseUrl}/level`, { level }));
    } catch (error) {
      console.error('Error setting level:', error);
      if (this.pendingLevelChange === level) {
        this.pendingLevelChange = null;
      }
      this.getUserLevel();
      throw error;
    } finally {
      if (this.pendingLevelChange === level) {
        this.pendingLevelChange = null;
      }
    }
  }

  trackDownload(payload: { download_type: string }): void {
    this.http
      .post<{ body: { status: boolean; download_type: string } }>(`${this.baseUrl}/downloads`, payload)
      .subscribe({
        next: (response) => {
          if (response && response.body && response.body.status) {
            this.fileDownloaded.set({ download_type: response.body.download_type });
          } else {
            this.fileDownloaded.set(null);
            console.warn('Error while tracking download:', response);
          }
        },
        error: (error) => {
          console.error('Error tracking download:', error);
        },
      });
  }

  /**
   * Adds multiple temporary chat messages to the current chat history
   * @param messages Array of chat messages with content and message_type
   */
  addTemporaryChatMessages(messages: { message: string; message_type: string }[]): void {
    const currentHistory = this.chatHistory() || [];
    const newMessages: ChatMessage[] = messages.map((msg, index) => ({
      message_id: `temp_${Date.now()}_${index}_${msg.message_type}`,
      content: msg.message,
      message_type: msg.message_type,
      timestamp: new Date().toISOString(),
      level_id: this.userLevel(),
      message_pair_id: null,
    }));
    this.chatHistory.set([...currentHistory, ...newMessages]);
  }

  /**
   * Adds persistent chat messages that survive history reloads
   * Used for chat start messages that should remain visible
   * @param messages Array of chat messages with content and message_type
   */
  addPersistentChatMessages(messages: { message: string; message_type: string }[]): void {
    const currentHistory = this.chatHistory() || [];
    const newMessages: ChatMessage[] = messages.map((msg, index) => ({
      message_id: `persistent_${Date.now()}_${index}_${msg.message_type}`,
      content: msg.message,
      message_type: msg.message_type,
      timestamp: new Date().toISOString(),
      level_id: this.userLevel(),
      message_pair_id: null,
      is_persistent: true,
    }));
    this.chatHistory.set([...currentHistory, ...newMessages]);
  }

  /**
   * Clears the entire chat history
   * Used when switching levels to prevent old messages from showing
   */
  clearChatHistory(): void {
    this.chatHistory.set([]);
  }

  public loadUserData(): void {
    this.getUserLevel();
    this.getUserAttempts();
    this.getUserAchievements();
    this.getUserHistory();
    this.getTotalScore();
    this.getUserLevelDuration();
    this.getAllLevels();
    this.getAllCompletedLevels();
    this.getHints();
  }

  /**
   * Loads user data without overwriting chat history and without fetching level
   * Used when switching levels to preserve cleared chat history and prevent race conditions
   * The level should be set via setLevel() and passed to LevelStartDialog via dialog data
   */
  public loadUserDataWithoutHistory(): void {
    // Intentionally NOT calling getUserLevel() to prevent race conditions
    // The level is already set via setLevel() and should be passed via dialog data
    this.getUserAttempts();
    this.getUserAchievements();
    this.getTotalScore();
    this.getUserLevelDuration();
    this.getAllLevels();
    this.getAllCompletedLevels();
    this.getHints();
  }

  trackBackgroundDownload(): void {
    this.trackDownload({ download_type: 'background_image' });
  }

  /**
   * Transforms ###IMG#value#IMG### patterns in chat message content to img tags
   * @param messages Array of chat messages to transform
   * @returns Transformed array of chat messages
   */
  private transformImageTags(messages: ChatMessage[]): ChatMessage[] {
    const imageTagPattern = /###IMG#([^#]+)#IMG###/g;
    return messages.map((msg) => ({
      ...msg,
      content: msg.content.replace(imageTagPattern, (_match, value) => {
        return `<img src='${this.imagePath}/level/${value}.jpg' alt='Image: ${value}' />`;
      }),
    }));
  }

  private createUserNames(): {
    firstName: string;
    lastName: string;
    fullName: string;
    userName: string;
    initials: string;
  } {
    let firstName = '';
    let lastName = '';
    let fullName = 'Unknown User';
    let userName = 'Unknown User';
    let initials = '??';

    const claims = this.authService.getUserInfo();

    if (claims) {
      if (claims.name) {
        const comma = claims.name.indexOf(',');
        if (comma !== -1) {
          firstName = claims.name.substring(comma + 1).trim();
          userName = firstName;
        }
      } else if (claims.preferred_username) {
        firstName = claims.preferred_username.split('@')[0];
      } else {
        firstName = claims.email?.split('@')[0] || '';
      }

      if (claims.name && claims.name.includes(',')) {
        const parts = claims.name.split(',').map((part: string) => part.trim());
        if (parts.length === 2) {
          lastName = parts[0];
        }

        fullName = `${firstName} ${lastName}`;
      } else {
        fullName = fullName || claims.name || claims.preferred_username || claims.email || 'Unknown User';
      }

      initials = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}` || '??';
    }

    return { firstName, lastName, fullName, userName, initials };
  }
}
